import React from 'react';
import { cn } from '../lib/utils';
import { badgeDotClasses, badgeToneClasses } from './badge';

type StatusTone = keyof typeof badgeToneClasses;

export type StatusType =
  | 'active'
  | 'inactive'
  | 'draft'
  | 'pending'
  | 'approved'
  | 'overdue'
  | 'completed'
  | 'cancelled'
  | 'scheduled'
  | 'in-progress'
  | 'review'
  | 'expired'
  | 'suspended';

export interface StatusBadgeProps {
  status: StatusType | (string & {});
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  dot?: boolean;
  /** Explicit severity when the visible label alone does not define urgency. */
  tone?: StatusTone;
}

export const statusTones: Record<string, StatusTone> = {
  critical: 'critical',
  major: 'danger',
  high: 'danger',
  moderate: 'warning',
  medium: 'warning',
  minor: 'information',
  low: 'information',
  active: 'success',
  'on track': 'success',
  'on-track': 'success',
  completed: 'success',
  saved: 'success',
  approved: 'success',
  inactive: 'neutral',
  draft: 'neutral',
  cancelled: 'neutral',
  pending: 'warning',
  review: 'warning',
  scheduled: 'information',
  'in-progress': 'information',
  overdue: 'danger',
  expired: 'danger',
  suspended: 'warning',
};

const defaultTone: StatusTone = 'neutral';

function formatLabel(status: string): string {
  const label = status.replace(/[-_]/g, ' ').toLowerCase();
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function StatusBadge({
  status,
  size = 'sm',
  className,
  dot = true,
  tone: explicitTone,
}: StatusBadgeProps) {
  const tone = explicitTone ?? statusTones[status.toLowerCase()] ?? defaultTone;

  return (
    <span
      className={cn(
        'inline-flex w-auto self-start justify-self-start max-w-full shrink-0 whitespace-nowrap items-center gap-1.5 rounded-badge border font-body font-medium tracking-normal',
        badgeToneClasses[tone],
        'px-2 py-0.5 text-caption',
        className,
      )}
      data-ui="badge"
      data-requested-size={size}
      title={formatLabel(status)}
      data-status-tone={tone}
      data-experience-tone={tone}
    >
      {dot && (
        <span
          className={cn('h-1.5 w-1.5 shrink-0 rounded-full', badgeDotClasses[tone])}
          aria-hidden="true"
        />
      )}
      <span className="min-w-0 truncate">{formatLabel(status)}</span>
    </span>
  );
}
